import { supabase } from '../lib/supabaseClient';
import logger from './logger';
import { subtitleDebug } from './subtitleDebug';

const cleanSubtitleUrl = (url) => (url ? String(url).replace(/\?$/, '') : null);

const rowsToSubtitleMap = (rows = []) =>
  rows.reduce((acc, row) => {
    const url = cleanSubtitleUrl(row.subtitle_url);
    if (row.language_code && url) {
      acc[row.language_code] = url;
    }
    return acc;
  }, {});

/**
 * Fetch subtitle URLs via backend API (preferred — bypasses Supabase RLS).
 */
export async function fetchStorySubtitleUrlsFromApi(apiClient, storyId) {
  if (!apiClient || !storyId) return {};

  subtitleDebug('storySubtitles', 'GET /stories/{id}/subtitles', { storyId });

  try {
    const response = await apiClient.getStorySubtitles(storyId);
    if (response?.status && response?.data && typeof response.data === 'object') {
      const urls = Object.fromEntries(
        Object.entries(response.data)
          .map(([lang, url]) => [lang, cleanSubtitleUrl(url)])
          .filter(([, url]) => url)
      );
      subtitleDebug('storySubtitles', 'GET /subtitles success', {
        storyId,
        langs: Object.keys(urls),
        urls,
      });
      return urls;
    }
    subtitleDebug('storySubtitles', 'GET /subtitles empty response', { storyId, response });
    logger.warn('[subtitles] API returned no subtitle data for story', storyId, response);
  } catch (error) {
    subtitleDebug('storySubtitles', 'GET /subtitles failed', { storyId, error });
    logger.warn('[subtitles] API fetch failed for story', storyId, error);
  }

  return {};
}

/**
 * Fetch subtitle URLs directly from Supabase (fallback if API unavailable).
 */
export async function fetchStorySubtitleUrls(storyId) {
  if (!storyId) return {};

  try {
    const { data, error } = await supabase
      .from('subtitles')
      .select('language_code, subtitle_url')
      .eq('story_id', storyId);

    if (error) {
      logger.warn('[subtitles] Supabase fetch failed for story', storyId, error);
      return {};
    }

    if (!data?.length) {
      logger.log('[subtitles] No subtitle rows found for story', storyId);
      return {};
    }

    return rowsToSubtitleMap(data);
  } catch (error) {
    logger.warn('[subtitles] Supabase fetch error for story', storyId, error);
    return {};
  }
}

/**
 * Resolve subtitle URLs from inline story fields and/or backend/Supabase lookup.
 */
export async function resolveStorySubtitles(story, apiClient = null) {
  if (!story) return {};

  if (story.subtitle_url) {
    return { en: cleanSubtitleUrl(story.subtitle_url) };
  }

  if (story.subtitle_urls && typeof story.subtitle_urls === 'object') {
    return Object.fromEntries(
      Object.entries(story.subtitle_urls)
        .map(([lang, url]) => [lang, cleanSubtitleUrl(url)])
        .filter(([, url]) => url)
    );
  }

  if (!story.id) return {};

  const fromApi = await fetchStorySubtitleUrlsFromApi(apiClient, story.id);
  if (Object.keys(fromApi).length > 0) {
    return fromApi;
  }

  return fetchStorySubtitleUrls(story.id);
}
